import '../PushProducts.scss';

import cn from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Button from '@/common/components/atoms/Button/Button';
import { SOURCE } from '@/common/constants';
import useProductsPredictions from '@/common/hooks/products/useProductsPredictions';
import { ReactComponent as InfoIcon } from '@/ebat/assets/icons/info.svg';
import Loader from '@/ebat/components/blocks/Common/Loader/Loader';
import Tooltip from '@/ebat/components/blocks/Common/Tooltip/Tooltip';
import ProductSlider from '@/ebat/components/blocks/ProductSlider/ProductSlider';

import {
  PushPredictionsDefaultProps,
  PushPredictionsPropTypes,
} from './PushPredictions.proptypes';

const PushPredictions = ({
  addToCartOptions,
  className,
  context,
  disabled,
  handleDisableIA,
  isLoading,
  limit,
  onProductsNotEmpty,
  products,
  refetchCart,
  slidesToShow,
  source,
}) => {
  const { t } = useTranslation();

  /**
   * Format products list to construct for API call payload
   * @type {*[]}
   */
  const formattedProducts = useMemo(
    () =>
      products.reduce((acc, item) => {
        if (item.product) {
          acc.push({
            id: item.product.path,
            quantity: item.quantity || 1,
          });
        }
        return acc;
      }, []),
    [products],
  );

  /**
   * Get predictions with given params
   */
  const {
    data: predictions,
    isLoading: isLoadingPredictions,
    isSuccess: isSuccessPredictions,
  } = useProductsPredictions({
    limit,
    source,
    products: [...formattedProducts],
    enabled: formattedProducts.length > 0,
  });

  const foramttedData = () =>
    predictions?.data?.map((prediction) => ({
      ...prediction,
      source: SOURCE.PUSH_PRODUCT,
    }));

  const isDisabled = useMemo(() => {
    const loading = isLoading || isLoadingPredictions;

    return {
      disabled: loading || disabled,
      loading,
      notDisplayable:
        !isLoading &&
        (predictions === undefined || predictions?.data.length === 0),
    };
  }, [disabled, isLoading, isLoadingPredictions, predictions]);

  const isProductsNotEmpty = useMemo(() => predictions?.data.length > 0, [
    predictions,
  ]);

  useEffect(() => {
    if (isProductsNotEmpty) {
      onProductsNotEmpty();
    }
  }, [isProductsNotEmpty, onProductsNotEmpty]);

  /**
   * Don't display the component
   */
  if (isDisabled.notDisplayable) {
    return null;
  }

  return (
    <div
      className={cn('push-products', 'push-products--predictions', className, {
        [`push-products--${source}`]: source,
      })}
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...{ 'data-qa-id': context || 'push-predictions' }}
    >
      <div className="wrapper">
        <div className="push-products__title">
          <h2 className="h2" data-qa-id="push-predictions-title">
            {t(`ebat:entities:common.push_products.${source}`)}
          </h2>
          {handleDisableIA && (
            <div className="push-products__hide-predictions hide-predictions">
              <Button
                className="hide-predictions__action"
                onClick={handleDisableIA}
              >
                {t(
                  'ebat:entities:common.push_products.hide_predictions.action',
                )}
              </Button>
              <Tooltip
                className="hide-predictions__tooltip"
                message={
                  <Trans
                    allowHTML
                    i18nKey={t(
                      'ebat:entities:common.push_products.hide_predictions.tooltip',
                    )}
                  />
                }
                position="top-left"
              >
                <InfoIcon className="hide-predictions__icon" />
              </Tooltip>
            </div>
          )}
        </div>
        {isSuccessPredictions && (
          <ProductSlider
            addToCartOptions={addToCartOptions}
            attributionToken={predictions?.meta?.attribution_token}
            className="push-products__slider"
            context="push-redictions"
            disabled={isDisabled.disabled}
            isLoading={isDisabled.loading}
            products={foramttedData()}
            refetchCart={refetchCart}
            slidesToShow={slidesToShow}
          />
        )}
        {isDisabled.loading && (
          <Loader type="full" className="push-products__loader" />
        )}
      </div>
    </div>
  );
};

PushPredictions.defaultProps = {
  ...PushPredictionsDefaultProps,
};

PushPredictions.propTypes = {
  ...PushPredictionsPropTypes,
};

export default PushPredictions;
